/**
 * Ajustes: la clave, el manuscrito y la ayuda.
 *
 * La clave de OpenRouter es del autor y se queda en su equipo —el motor la guarda en su disco y
 * nosotros no la vemos nunca—. Esa decisión tiene un precio: el autor tiene que ir a otra web,
 * crear una cuenta y meter una tarjeta. Esta pantalla existe para que ese trámite se haga una
 * vez, con los pasos delante, y para que sepa AL MOMENTO si ha quedado bien.
 *
 * Porque el fallo caro no es pegar mal la clave: es pegar una clave válida de una cuenta sin
 * crédito. Parece que todo va bien hasta que, media hora después, un colaborador se cae con un
 * error de OpenRouter que no dice nada.
 *
 * En pantalla solo se enseña la pista (`sk-or…7f2a`): la clave entera no se muestra nunca.
 */
import { useCallback, useEffect, useState } from "react";
import { useClerk } from "@clerk/react";
import { PROMESA_PRIVACIDAD, ajustes, guardarAjustes, clave as pedirClave, descargas, dolares, estadoSesion, hayNueva, ponerPassword, salir, sistema } from "../api";
import type { Config, Clave, Descargas, Salud } from "../api";
import { versionDeTuSistema } from "../logica";

export default function Ajustes({ est }: { est: Salud | null }) {
  const [cfg, setCfg] = useState<Config | null>(null);
  const [est2, setEst2] = useState<Clave | null>(null);
  const [texto, setTexto] = useState("");
  const [estado, setEstado] = useState<string | null>(null);
  const [cerrado, setCerrado] = useState<boolean | null>(null);
  const [nueva, setNueva] = useState("");
  const [anterior, setAnterior] = useState("");
  const [candado, setCandado] = useState<string | null>(null);
  // Con un motor de Cuentas III y la cuenta enlazada, la contraseña ya no existe: lo que hay que
  // enseñar es a qué está atado este ordenador y cómo soltarlo.
  const [conCuenta, setConCuenta] = useState(false);
  // El instalador que le toca a este equipo. El sistema se mira una vez: no cambia a media sesión.
  const [desc, setDesc] = useState<Descargas | null>(null);
  const [sist] = useState(sistema);
  // La última versión es la de SU sistema: Mac y Windows pueden ir a distinta (logica.ts).
  const suya = desc ? versionDeTuSistema(desc, sist) : null;
  const clerk = useClerk();

  const mirar = useCallback(() => { pedirClave().then(setEst2).catch(() => {}); }, []);
  useEffect(() => {
    ajustes().then(setCfg).catch(() => {});
    estadoSesion().then((e) => { setCerrado(e.cerrado); setConCuenta(!!e.cuentas && !!e.cuenta); }).catch(() => {});
    descargas().then(setDesc).catch(() => {});
    mirar();
  }, [mirar]);

  async function echarCandado() {
    if (nueva.length < 6) { setCandado("La contraseña necesita al menos 6 caracteres."); return; }
    setCandado("Guardando…");
    try {
      await ponerPassword(nueva, anterior || undefined);
      setCerrado(true); setNueva(""); setAnterior("");
      setCandado("Listo. A partir de ahora el panel te la pedirá al abrirlo.");
    } catch (e) {
      setCandado(e instanceof Error ? e.message : "No se ha podido guardar.");
    }
  }

  async function cambiarEnvio(optOut: boolean) {
    setCfg((c) => (c ? { ...c, usageOptOut: optOut } : c));
    await guardarAjustes({ usageOptOut: optOut }).catch(() => {});
  }

  async function guardar() {
    const k = texto.trim();
    if (!k) return;
    setEstado("Comprobándola con OpenRouter…");
    try {
      setCfg(await guardarAjustes({ openrouter_key: k }));
      setTexto("");
      const c = await pedirClave();
      setEst2(c);
      setEstado(
        c.estado === "buena" && !c.sinCredito ? null
          : c.estado === "rechazada" ? "OpenRouter no reconoce esa llave. Cópiala otra vez, entera."
          : c.estado === "sin_red" ? "Guardada, pero no he podido comprobarla: sin conexión."
          : null,
      );
    } catch (e) {
      setEstado(e instanceof Error ? e.message : "No se pudo guardar.");
    }
  }

  const c = est2;
  const buena = c?.estado === "buena" && !c.sinCredito;

  return (
    <>
      <section className="carta">
        <h2>Tu llave de OpenRouter
          <span className="sub">
            {c?.estado === "buena" ? (c.sinCredito ? "sin crédito" : "funcionando")
              : c?.estado === "rechazada" ? "no válida"
              : c?.estado === "sin_clave" ? "sin poner" : "…"}
          </span>
        </h2>

        {buena && (
          <>
            <div className="cifra">
              <b>{c.queda !== null && c.queda !== undefined ? dolares(c.queda) : dolares(c.gastadoMes ?? 0)}</b>
              <span>{c.queda !== null && c.queda !== undefined ? "te quedan en OpenRouter" : "gastado este mes"}</span>
            </div>
            <p className="vacio">
              {c.etiqueta} · {dolares(c.gastado ?? 0)} desde que abriste la cuenta
              {c.tope ? ` · límite de la clave: ${dolares(c.tope)}` : ""}. El consumo lo
              pagas tú directamente a OpenRouter, en dólares, con tus modelos y tus límites.
            </p>
            {/* M-08: la promesa, escrita con precisión y donde está la llave. */}
            <p className="vacio">{PROMESA_PRIVACIDAD}</p>
          </>
        )}

        {c?.sinCredito && (
          <p className="vacio" style={{ color: "var(--acc)" }}>
            La llave es correcta, pero esa cuenta de OpenRouter <b>no tiene crédito</b>. Entra en
            openrouter.ai/credits y carga 10 $ para empezar: da para un informe completo de una
            novela y varias pasadas de reescritura.
          </p>
        )}

        {c?.estado === "sin_clave" && (
          <>
            <p className="vacio">
              HumanInk no te cobra el consumo: lo pagas tú, directo al proveedor, y así tu texto
              no pasa por nosotros. Se hace una vez y son tres minutos.
            </p>
            <ol className="pasos">
              <li>Entra en <b>openrouter.ai</b> y crea una cuenta.</li>
              <li>En <b>Credits</b>, carga 10 $. Es tu saldo, no una suscripción.</li>
              <li>En <b>Keys</b>, pulsa <b>Create key</b> y cópiala entera.</li>
              <li>Pégala aquí abajo.</li>
            </ol>
          </>
        )}

        <div className="dir">
          <input
            className="escribir" type="password" autoComplete="off"
            placeholder={cfg?.keyHint ?? "sk-or-v1-…"}
            value={texto} onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") guardar(); }}
          />
          <button className="btn" onClick={guardar} disabled={!texto.trim()}>
            {cfg?.hasKey ? "Cambiar" : "Guardar"}
          </button>
        </div>
        {estado && <p className="vacio">{estado}</p>}
      </section>

      {/* EL CANDADO. La web dice desde el primer día «el panel se cierra con una contraseña que
          solo existe en ese ordenador», y hasta hoy no había DÓNDE ponerla: el motor tenía la
          ruta hecha y el panel no la enseñaba, así que la promesa era literalmente irrealizable.
          Sin contraseña, además, el candado de las rutas que leen y escriben tu libro no existe.
          DESDE CUENTAS III (11-sep) esa contraseña se retira al entrar con cuenta, y esta sección
          pasa a decir lo que es verdad: el ordenador lo protege la cuenta a la que está atado. */}
      {conCuenta ? (
      <section className="carta">
        <h2>Este ordenador<span className="sub">lo protege tu cuenta</span></h2>
        <p className="vacio">
          Tu manuscrito solo se abre entrando con tu cuenta, y este ordenador está enlazado a ella:
          otra cuenta no puede abrirlo. Si vas a dejar de usarlo o se lo pasas a otra persona,
          desvincúlalo.
        </p>
        <div className="dir">
          <button className="btn fantasma" onClick={() => { salir(true).finally(() => clerk.signOut()); }}>
            Desvincular este ordenador y cerrar sesión
          </button>
        </div>
      </section>
      ) : (
      <section className="carta">
        <h2>El candado del panel
          <span className="sub">{cerrado === null ? "…" : cerrado ? "puesto" : "sin poner"}</span>
        </h2>
        <p className="vacio">
          {cerrado
            ? "El panel te pide la contraseña al abrirlo. Puedes cambiarla aquí."
            : "Ahora mismo cualquiera que use este ordenador puede abrir el panel y leer tu libro. Si lo compartes, ponle una contraseña."}
        </p>
        <p className="vacio">
          Vive solo en este equipo y no se guarda: se guarda su huella. No hay servidor al que
          pedirla ni nadie a quien reclamársela — <b>si la pierdes, la pierdes</b>.
        </p>
        {cerrado && (
          <div className="escribir">
            <input type="password" value={anterior} placeholder="La contraseña actual"
                   onChange={(e) => setAnterior(e.target.value)} />
          </div>
        )}
        <div className="escribir">
          <input type="password" value={nueva} placeholder="Una contraseña de 6 caracteres o más"
                 onChange={(e) => setNueva(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") echarCandado(); }} />
          <button className="btn" onClick={echarCandado} disabled={nueva.length < 6}>
            {cerrado ? "Cambiarla" : "Poner contraseña"}
          </button>
        </div>
        {candado && <p className="vacio">{candado}</p>}
      </section>
      )}

      {/* LO QUE SE ENVÍA. El opt-out existía en el motor y solo se podía tocar desde Word, así que
          quien entra por el Studio no tenía forma de apagarlo. Un opt-out sin interruptor es una
          casilla que solo conocemos nosotros. */}
      <section className="carta">
        <h2>Lo que nos llega<span className="sub">{cfg?.usageOptOut || cfg?.telemetria === false ? "nada" : "dos avisos anónimos"}</span></h2>
        {/* Un motor con la telemetría apagada (4-oct) lo dice: la tarjeta no puede prometer «dos avisos» que ya no salen. */}
        {cfg?.telemetria === false && (
          <p className="aviso">
            Ahora mismo el motor de tu ordenador <b>no nos manda nada</b>: ni el recuento de uso ni los avisos de error.
            Lo de abajo describe lo que mandaría si se volviera a encender; el botón no cambia nada mientras tanto.
          </p>
        )}
        {/* 17-sep (Rais): «esto de enviar medidas no está explicado». Lo que manda usage.mjs, dicho
            entero: si aquí falta algo que el motor envía, esta tarjeta miente. */}
        <p className="vacio">
          Para saber qué se usa y arreglar lo que falla, el motor de tu ordenador nos manda dos cosas.
          Van con el número de esta instalación, que se creó al azar: no lleva tu nombre ni tu email,
          pero si tienes cuenta o suscripción está asociado a ella.
        </p>
        <dl className="ficha-actual">
          <div><dt>Una vez al día</dt><dd>Cuántos encargos has hecho a cada agente, tu plan, la versión del motor y si es Mac o Windows.</dd></div>
          <div><dt>Si algo falla</dt><dd>Dónde ha fallado, el tipo de error, su mensaje (hasta 200 caracteres) y la línea del código. Las rutas de tu disco se borran antes de salir.</dd></div>
          <div><dt>Nunca</dt><dd><b>Tu texto, el título de tu libro, tus documentos ni tu llave de OpenRouter.</b></dd></div>
        </dl>
        <p className="vacio">
          Si lo apagas, dejan de salir las dos cosas. Lo que sigue funcionando igual necesita hablar con
          nosotros de todos modos: comprobar tu suscripción y avisarte de versiones nuevas.
        </p>
        <div>
          <button className="btn fantasma" onClick={() => cambiarEnvio(!cfg?.usageOptOut)}>
            {cfg?.usageOptOut ? "Volver a enviar las medidas" : "No enviar nada"}
          </button>
        </div>
      </section>


      {/* EL INSTALADOR, DESDE DENTRO (R4.3). Un alumno que entra en studio.humanink.io desde un
          ordenador nuevo tiene que poder bajarse el motor aquí, sin buscar un enlace en un correo
          y sin que nadie le diga qué fichero le toca. Si no hay manifiesto, la tarjeta no existe:
          un botón que no sabe a qué apunta es peor que ninguno. */
      desc && (
        <section className="carta">
          {/* 17-sep (Rais): «debería poner el número del motor». Arriba decía la ÚLTIMA versión, no
              la que tiene instalada, y no había forma de saber si hacía falta actualizar. */}
          <h2>El motor en tu equipo<span className="sub">{est?.version ? `tienes la ${est.version}` : "sin motor"} · última {suya!.version}</span></h2>
          <p className="vacio">
            {!est?.version
              ? <>No encuentro el motor en este ordenador. Instálalo con el botón de abajo.</>
              : hayNueva(est.version, suya!.version)
                ? <><b>Hay una versión nueva.</b> Tienes la {est.version} y la última es la {suya!.version}{suya!.publicado ? `, del ${suya!.publicado}` : ""}: descárgala e instálala encima; no se pierde nada.</>
                : <>Estás al día: tienes la {est.version}, que es la última.</>}
          </p>
          <p className="vacio">
            El motor es lo que hace el trabajo en tu ordenador: tu manuscrito y tu llave de
            OpenRouter no salen de ahí. Cuando hay una versión nueva te avisamos arriba y se instala desde aquí.
          </p>
          <div className="dir">
            {(!sist || sist === "mac") && desc.pkg && (
              <a className={`btn${sist === "mac" ? "" : " fantasma"}`} href={desc.pkg}>Descargar para Mac</a>
            )}
            {/* 23-sep: Mac y Windows pueden ir a distinta versión. Se dice, no se esconde. */}
            {desc.winVersion && desc.winVersion !== desc.version && (
              <p className="vacio">{sist === "win" ? `En Mac, la última es la ${desc.version}.` : `En Windows, la última es la ${desc.winVersion}.`}</p>
            )}
            {(!sist || sist === "win") && desc.win && (
              <a className={`btn${sist === "win" ? "" : " fantasma"}`} href={desc.win}>Descargar para Windows</a>
            )}
            {sist === "mac" && desc.win && <a className="btn fantasma" href={desc.win}>…o para Windows</a>}
            {sist === "win" && desc.pkg && <a className="btn fantasma" href={desc.pkg}>…o para Mac</a>}
          </div>
          {/* Lo que no está firmado se dice ANTES de que lo diga el sistema operativo con un
              aviso que da miedo. El aviso va con el ENLACE, no con el sistema detectado: en un Mac
              también se ofrece el .exe —para instalarlo en otro equipo— y esconderle ahí que no
              está firmado sería justo la sorpresa que este aviso existe para evitar. */}
          {desc.pkg && desc.pkgFirmado === false && (
            <p className="vacio">
              Este instalador de Mac todavía no está firmado: macOS dirá que no puede verificar al
              desarrollador. Se abre con el botón derecho sobre el fichero y «Abrir».
            </p>
          )}
          {desc.win && desc.winFirmado === false && (
            <p className="vacio">
              El instalador de Windows todavía no está firmado: Windows te avisará de que no
              reconoce al editor. Se pulsa «Más información» y «Ejecutar de todos modos».
              {desc.winZip ? <> Si Defender lo bloquea del todo, está también <a href={desc.winZip}>en zip</a>.</> : null}
            </p>
          )}
          {desc.notes && <p className="vacio">{desc.notes}</p>}
        </section>
      )}

    </>
  );
}
